import { NetworkProps, enabledNetworks } from '@dapp/multi-lib/network';
import { useToast } from '@dapp/multi-lib/ui/toast';
import { OptionProfile, StatefulMenu } from 'baseui/menu';
import { PLACEMENT, StatefulPopover } from 'baseui/popover';
import { LabelSmall } from 'baseui/typography';
import { cx } from 'class-variance-authority';
import { isNil } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import { useConnection, useSwitchChain } from 'wagmi';
import styles from './migrationpanel.module.css';
import Image from 'next/image';
import { getStaticAssetUrl } from 'utils/url';
import {
  BridgeNetworkList,
  BridgeNetworkOption,
} from 'structure/bridge/components/BridgeNetworkList/BridgeNetworkList';

type INetworks = BridgeNetworkOption;

export const NetworkSelection: React.FC<{
  allowedNetworks?: number[];
  rightAligned?: boolean;
  embedded?: boolean;
}> = ({ allowedNetworks, rightAligned = false, embedded = false }) => {
  const [networks, setNetworks] = useState<INetworks[]>([]);
  const [selectNetworkOpen, setSelectNetworkOpen] = useState(false);

  const { chain } = useConnection();
  const { switchChain } = useSwitchChain();
  const { t } = useTranslation('migrate');
  const toastV2 = useToast();

  useEffect(() => {
    const supportedNetworks = enabledNetworks
      .filter((item) => {
        if (allowedNetworks && allowedNetworks.length > 0) {
          return allowedNetworks.includes(item.id);
        }
        return false;
      })
      .map((n) => {
        return {
          id: n.id,
          network: n,
          subtitle: n.name,
          imgUrl: `${process.env.NEXT_PUBLIC_STATIC_ASSETS_BUCKET}/sfi/icons/tokens/${n.shortName}.png`,
        };
      });

    setNetworks(supportedNetworks);
  }, [allowedNetworks]);

  const network = useMemo(() => {
    const selectedNetworkId = chain?.id;
    if (
      isNil(selectedNetworkId) ||
      !networks.find((obj) => obj.id === selectedNetworkId)
    ) {
      return networks[0];
    } else {
      return networks.find((n) => n.network.id === selectedNetworkId);
    }
  }, [chain?.id, networks]);
  const handleNetworkChange = (chain: NetworkProps) => {
    try {
      switchChain({ chainId: chain.id });
    } catch (error) {
      console.log('### error', error);
      toastV2({
        title: t('common:ERROR_MESSAGES.ERROR'),
        description: t('common:NETWORK_CHANGE_ERROR'),
        type: 'error',
      });
    }
  };

  const networkSelector = (
    <button
      type='button'
      aria-expanded={rightAligned || embedded ? selectNetworkOpen : undefined}
      className={cx(
        'cursor-pointer text-white',
        embedded
          ? 'flex h-full min-w-0 max-w-[132px] items-center gap-2 px-2 sm:max-w-[210px] sm:px-4'
          : 'w-full'
      )}
      onClick={() => {
        if (rightAligned || embedded) {
          setSelectNetworkOpen((open) => !open);
        }
      }}
    >
      <div
        className={cx(
          'flex min-w-0 items-center text-white',
          embedded
            ? 'gap-2'
            : 'w-full justify-between rounded-[20px] bg-[#191A1E] px-4',
          !embedded && (rightAligned ? 'h-[42px]' : 'p-4')
        )}
      >
        <div
          className={cx('flex min-w-0 items-center gap-2', {
            'ml-auto': rightAligned && !embedded,
          })}
        >
          <Image
            src={
              network?.imgUrl ??
              getStaticAssetUrl('/sfi/static/svgs/arrow-down.svg')
            }
            className='rounded-full'
            alt='TokenIcon'
            width={24}
            height={24}
          />
          <LabelSmall
            className={cx(
              twMerge(
                styles.migrateFont,
                embedded && styles.responsiveLabel,
                'truncate text-lg'
              )
            )}
          >
            {network?.network.name}
          </LabelSmall>
        </div>
        <div
          className={cx('shrink-0', {
            'ml-2 mr-1': rightAligned && !embedded,
          })}
        >
          <Image
            src={getStaticAssetUrl('/sfi/static/svgs/arrow-down.svg')}
            width={16}
            height={16}
            alt='arrow-down'
          />
        </div>
      </div>
    </button>
  );

  if (embedded) {
    return (
      <>
        {networkSelector}
        <BridgeNetworkList
          open={selectNetworkOpen}
          options={networks}
          fullWidthOverlay
          onClickOutside={() => setSelectNetworkOpen(false)}
          onChange={(option) => {
            handleNetworkChange(option.network);
            setSelectNetworkOpen(false);
          }}
        />
      </>
    );
  }

  return (
    <>
      <LabelSmall
        className={cx(twMerge(styles.label, 'mb-2 text-sm text-white-600'))}
      >
        {t('NETWORK')}
      </LabelSmall>
      {rightAligned ? (
        <>
          {networkSelector}
          <BridgeNetworkList
            open={selectNetworkOpen}
            options={networks}
            onClickOutside={() => setSelectNetworkOpen(false)}
            onChange={(option) => {
              handleNetworkChange(option.network);
              setSelectNetworkOpen(false);
            }}
          />
        </>
      ) : (
        <StatefulPopover
          focusLock
          placement={PLACEMENT.bottom}
          content={({ close }) => (
            <StatefulMenu
              items={networks}
              onItemSelect={(n) => {
                handleNetworkChange(n.item.network);
                close();
              }}
              overrides={{
                List: {
                  style: {
                    maxHeight: '220px',
                    height: '100%',
                    backgroundColor: 'white',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderTopLeftRadius: '0px',
                    borderTopRightRadius: '0px',
                    borderBottomLeftRadius: '0px',
                    borderBottomRightRadius: '0px',
                    border: '1px solid #B5B5B5 !important',
                    background: '#000000',
                    color: 'var(--style-20-ffffff-light-text, #FFF)',
                  },
                },
                Option: {
                  component: OptionProfile,
                  props: {
                    getProfileItemLabels: ({
                      subtitle,
                    }: {
                      subtitle: string;
                    }) => ({
                      subtitle,
                    }),
                    getProfileItemImg: (item: INetworks) => item.imgUrl,
                    getProfileItemImgText: (item: INetworks) => item.subtitle,
                    overrides: {
                      ListItemProfile: {
                        style: {
                          paddingTop: '0px',
                          paddingBottom: '0px',
                          paddingLeft: '0px',
                          paddingRight: '0px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'start',
                        },
                      },
                      ProfileImg: {
                        style: {
                          width: '32px',
                          height: '32px',
                        },
                      },
                      ProfileLabelsContainer: {
                        style: {
                          marginRight: '24px',
                          marginTop: '24px',
                          marginBottom: '24px',
                          marginLeft: '-8px',
                        },
                      },
                      ProfileSubtitle: {
                        style: {
                          'font-size': '14px',
                          'font-family': 'Montserrat !important',
                          'font-style': 'normal',
                          'font-weight': 500,
                          'line-height': '22px',
                        },
                      },
                    },
                  },
                },
              }}
            />
          )}
        >
          {networkSelector}
        </StatefulPopover>
      )}
    </>
  );
};
